/**
 * Hook stato configuratore CONTROSOFFITTI (F17.2.3).
 *
 * Variante di `useConfiguratorState` adattata al dominio ceiling. Riusa LayerV2,
 * CertificationData, gli helper di tipologia/sistema/viti. Aggiunge stato
 * ceiling-specific:
 *
 *  - `ceilingDropMm`: altezza pendinatura (distanza solaio ↔ controsoffitto)
 *  - `attachmentType`: 'fixed' (a contatto) | 'suspended' (pendinato)
 *  - `hangerSpacingPrimaryMm`: passo pendini lungo orditura primaria
 *  - `hangerSpacingPerpendicularMm`: passo pendini perpendicolare
 *  - `primarySpacingMm`: passo orditura primaria (mm)
 *  - `secondarySpacingMm`: passo orditura secondaria (mm) — null se orditura singola
 *  - `perimeterProfileMlPerSqm`: ratio ml profilo perimetrale per m² (null = calcolato 4·√area)
 *
 * Forza `type = 'ceiling'` al save. Differenze rispetto al wall configurator:
 *  - Niente `studSpacingMm` (è "primarySpacingMm" qui)
 *  - I "pannelli a vista" (ceiling_tile) sono trattati come board nel sistema viti
 *  - I "pendini di sospensione" sono materiali di categoria accessory che vanno
 *    nel preventivo come accessori (non layer della stratigrafia)
 *
 * Vedi `Volt/06_TASKS/F17-controsoffitti-plan.md` per il piano completo.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStratigraphy } from '@/hooks/useStratigraphy';
import { useIntegratedStratigraphySave } from '@/hooks/useIntegratedStratigraphySave';
import { useMaterials, type DatabaseMaterial } from '@/hooks/useMaterials';
import { toast } from 'sonner';
import type { LayerV2 } from '../types';
import { useScrewSuggestions } from './useScrewSuggestions';
import { computeScrewCostPerSqm } from '@/utils/screwPricing';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import type { CertificationData, NameConflict } from './useConfiguratorState';

/** Tipo attacco al solaio: 'fixed' = a contatto, 'suspended' = pendinato. */
export type CeilingAttachmentType = 'fixed' | 'suspended';

/** Stato dimensionale specifico del controsoffitto (non condiviso con pareti). */
export interface CeilingDimensionalState {
  /** Distanza in mm tra solaio esistente e nuovo controsoffitto. Default 200. */
  ceilingDropMm: number;
  /** Modalità attacco: fixed = a contatto solaio, suspended = pendinato. Default 'suspended'. */
  attachmentType: CeilingAttachmentType;
  /** Passo pendini lungo l'orditura primaria (mm). Default 900. */
  hangerSpacingPrimaryMm: number;
  /** Passo pendini perpendicolare all'orditura primaria (mm). Default 900. */
  hangerSpacingPerpendicularMm: number;
  /** Passo orditura primaria (mm). Default 1000. */
  primarySpacingMm: number;
  /** Passo orditura secondaria (mm). null se orditura singola (tipologie CS-FIX-SINGLE / CS-SUSP-SINGLE). */
  secondarySpacingMm: number | null;
  /**
   * Ratio ml profilo perimetrale per m². null = calcolato dinamicamente come 4·√area
   * al momento "Aggiungi a preventivo". Override manuale per stanze L-shape / non quadrate.
   */
  perimeterProfileMlPerSqm: number | null;
}

const DEFAULT_CEILING_STATE: CeilingDimensionalState = {
  ceilingDropMm: 200,
  attachmentType: 'suspended',
  hangerSpacingPrimaryMm: 900,
  hangerSpacingPerpendicularMm: 900,
  primarySpacingMm: 1000,
  secondarySpacingMm: 500,
  perimeterProfileMlPerSqm: null,
};

/** Genera UUID v4 client-side per id layer locali. */
const newId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

interface UseCeilingConfiguratorStateProps {
  /** ID stratigrafia ceiling esistente da caricare. null/undefined = nuova. */
  stratigraphyId?: string | null;
  /** Layer iniziali pre-caricati (per flusso "crea da snapshot"). */
  initialLayers?: LayerV2[];
  /** Nome iniziale opzionale. */
  initialName?: string;
  /** Stato dimensionale iniziale (override defaults — es. caricato da una tipologia sistema). */
  initialCeiling?: Partial<CeilingDimensionalState>;
}

interface UseCeilingConfiguratorStateReturn {
  // ---- State condiviso con wall ----
  id: string | null;
  name: string;
  description: string;
  isCertified: boolean;
  certification: CertificationData;
  layers: LayerV2[];
  isLoading: boolean;
  isDirty: boolean;
  isSaving: boolean;
  loadError: string | null;
  totalThicknessMm: number;
  // ---- State ceiling-specific ----
  ceiling: CeilingDimensionalState;
  // ---- Actions ----
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  addLayer: (initial?: Partial<LayerV2>) => string;
  updateLayer: (id: string, patch: Partial<LayerV2>) => void;
  removeLayer: (id: string) => void;
  duplicateLayer: (id: string) => string | null;
  reorderLayers: (orderedIds: string[]) => void;
  setMaterial: (layerId: string, material: DatabaseMaterial) => void;
  setCertification: (patch: Partial<CertificationData> & { isCertified?: boolean }) => void;
  setScrewMaterial: (layerId: string, screw: DatabaseMaterial | null) => void;
  setScrewQuantity: (layerId: string, quantity: number) => void;
  reapplyScrewSuggestion: (layerId: string) => void;
  /** Aggiorna lo stato ceiling-specific (subset di campi). */
  setCeiling: (patch: Partial<CeilingDimensionalState>) => void;
  /** Auto-imposta i defaults ceiling da una tipologia sistema (CS-FIX-SINGLE / CS-SUSP-DOUBLE / ecc.). */
  applyTypology: (typology: {
    structure_type: 'single' | 'double';
    attachment_type: 'fixed' | 'suspended';
    hanger_spacing_mm: number | null;
    primary_profile_spacing_mm: number | null;
    secondary_profile_spacing_mm: number | null;
  }) => void;
  checkNameConflict: () => Promise<NameConflict | null>;
  saveAsync: () => Promise<string | null>;
  save: () => void;
  canSave: boolean;
}

const EMPTY_CERTIFICATION: CertificationData = {
  supplier: null,
  certificationCode: null,
  certificationLab: null,
  certificationDate: null,
  certificationNotes: null,
  acousticPerformance: null,
  acousticRwC: null,
  acousticRwCtr: null,
  acousticTestNorm: null,
  fireResistanceClass: null,
  fireReactionClass: null,
  fireTestNorm: null,
  thermalPerformance: null,
  thermalUValue: null,
  thermalRValue: null,
  weightPerSqm: null,
  mechanicalMaxHeightM: null,
  mechanicalLoadNSqm: null,
  mechanicalSuspendableLoadKg: null,
};

export function useCeilingConfiguratorState({
  stratigraphyId,
  initialLayers,
  initialName,
  initialCeiling,
}: UseCeilingConfiguratorStateProps): UseCeilingConfiguratorStateReturn {
  const { data: loaded, isLoading, error: loadError } = useStratigraphy(stratigraphyId ?? undefined);
  const saveMutation = useIntegratedStratigraphySave();
  const { data: catalog = [] } = useMaterials();
  const { currentOrganizationId } = useCurrentOrganization();

  const [id, setId] = useState<string | null>(stratigraphyId ?? null);
  const [name, setNameRaw] = useState('');
  const [description, setDescriptionRaw] = useState('');
  const [isCertified, setIsCertified] = useState(false);
  const [certification, setCertificationState] = useState<CertificationData>(EMPTY_CERTIFICATION);
  const [layers, setLayers] = useState<LayerV2[]>([]);
  const [ceiling, setCeilingState] = useState<CeilingDimensionalState>({
    ...DEFAULT_CEILING_STATE,
    ...(initialCeiling ?? {}),
  });
  const [isDirty, setIsDirty] = useState(false);

  // Hydrate da DB quando arriva una stratigrafia esistente
  useEffect(() => {
    if (!loaded) return;
    setId(loaded.id);
    setNameRaw(loaded.name ?? '');
    setDescriptionRaw(loaded.description ?? '');
    setIsCertified(Boolean((loaded as { is_certified?: boolean }).is_certified));
    const l = loaded as Record<string, unknown>;
    setCertificationState({
      supplier: (l.supplier_name as string) ?? null,
      certificationCode: (l.certification_code as string) ?? null,
      certificationLab: (l.certification_lab as string) ?? null,
      certificationDate: (l.certification_date as string) ?? null,
      certificationNotes: (l.certification_notes as string) ?? null,
      acousticPerformance: l.acoustic_performance != null ? Number(l.acoustic_performance) : null,
      acousticRwC: l.acoustic_rw_c_correction != null ? Number(l.acoustic_rw_c_correction) : null,
      acousticRwCtr: l.acoustic_rw_ctr_correction != null ? Number(l.acoustic_rw_ctr_correction) : null,
      acousticTestNorm: (l.acoustic_test_norm as string) ?? null,
      fireResistanceClass: (l.fire_resistance_class as string) ?? null,
      fireReactionClass: (l.fire_reaction_class as string) ?? null,
      fireTestNorm: (l.fire_test_norm as string) ?? null,
      thermalPerformance: l.thermal_performance != null ? Number(l.thermal_performance) : null,
      thermalUValue: l.thermal_u_value != null ? Number(l.thermal_u_value) : null,
      thermalRValue: l.thermal_r_value != null ? Number(l.thermal_r_value) : null,
      weightPerSqm: l.weight_per_sqm != null ? Number(l.weight_per_sqm) : null,
      mechanicalMaxHeightM: l.mechanical_max_height_m != null ? Number(l.mechanical_max_height_m) : null,
      mechanicalLoadNSqm: l.mechanical_load_n_sqm != null ? Number(l.mechanical_load_n_sqm) : null,
      mechanicalSuspendableLoadKg: l.mechanical_suspendable_load_kg != null ? Number(l.mechanical_suspendable_load_kg) : null,
    });

    // Campi ceiling-specific
    const attachment = (l.attachment_type as string) === 'fixed' || (l.attachment_type as string) === 'suspended'
      ? (l.attachment_type as CeilingAttachmentType)
      : DEFAULT_CEILING_STATE.attachmentType;
    setCeilingState({
      ceilingDropMm: l.ceiling_drop_mm != null ? Number(l.ceiling_drop_mm) : DEFAULT_CEILING_STATE.ceilingDropMm,
      attachmentType: attachment,
      hangerSpacingPrimaryMm: l.hanger_spacing_primary_mm != null
        ? Number(l.hanger_spacing_primary_mm)
        : DEFAULT_CEILING_STATE.hangerSpacingPrimaryMm,
      hangerSpacingPerpendicularMm: l.hanger_spacing_perpendicular_mm != null
        ? Number(l.hanger_spacing_perpendicular_mm)
        : DEFAULT_CEILING_STATE.hangerSpacingPerpendicularMm,
      // Reuse `stud_spacing_mm` for primary spacing (compat con DB esistente che già
      // usa stud_spacing_mm per le pareti; per ceiling lo riusiamo per il passo primario).
      primarySpacingMm: (l.stud_spacing_mm as number) ?? DEFAULT_CEILING_STATE.primarySpacingMm,
      secondarySpacingMm: l.secondary_spacing_mm != null
        ? Number(l.secondary_spacing_mm)
        : DEFAULT_CEILING_STATE.secondarySpacingMm,
      perimeterProfileMlPerSqm: l.perimeter_profile_ml_per_sqm != null
        ? Number(l.perimeter_profile_ml_per_sqm)
        : null,
    });

    const hydrated: LayerV2[] = (loaded.layers ?? []).map((lay: any, idx: number) => ({
      id: lay.id ?? newId(),
      position: lay.position ?? idx + 1,
      materialId: lay.materialId ?? null,
      material: lay.material ?? null,
      thickness: Number(lay.thickness ?? lay.material?.thickness ?? 0),
      interAxis: lay.interAxis ?? lay.inter_axis ?? undefined,
      screwMaterialId: lay.screwMaterialId ?? null,
      screwMaterial: lay.screwMaterial ?? null,
      screwQuantity: lay.screwQuantity ?? undefined,
      screwCostPerSqm: lay.screwCostPerSqm ?? undefined,
      screwIsAutoSuggested: false,
    }));
    setLayers(hydrated);
    setIsDirty(false);
  }, [loaded]);

  // Hydrate "da snapshot orfano" (analogo wall configurator)
  const initialHydratedRef = useRef(false);
  useEffect(() => {
    if (initialHydratedRef.current) return;
    if (stratigraphyId) return;
    if (!initialLayers || initialLayers.length === 0) return;
    initialHydratedRef.current = true;
    setLayers(
      initialLayers.map((l, idx) => ({
        ...l,
        id: l.id ?? newId(),
        position: l.position ?? idx + 1,
      })),
    );
    if (initialName) setNameRaw(initialName);
    setIsDirty(true);
  }, [initialLayers, initialName, stratigraphyId]);

  // Suggestions viti per ogni layer board/ceiling_tile (riuso il sistema wall — i pannelli
  // a vista usano vite SN30 simile alle lastre fonoassorbenti). La tipologia è hardcoded
  // a 'ceiling' qui per scegliere le regole spacing corrette dal DB.
  const { suggestions: screwSuggestions } = useScrewSuggestions(layers, 'ceiling');

  // Auto-apply viti (stessa logica wall, ma sui layer board/ceiling_tile)
  useEffect(() => {
    if (screwSuggestions.size === 0) return;
    let changed = false;
    setLayers(prev => {
      const next = prev.map(l => {
        const cat = l.material?.category;
        if (cat !== 'board' && cat !== 'ceiling_tile') return l;
        const sug = screwSuggestions.get(l.id);
        if (!sug || !sug.recommended) return l;

        if (l.screwIsAutoSuggested === false) {
          const currentLengthMm = Number(l.screwMaterial?.length ?? l.screwMaterial?.thickness ?? 0);
          const requiredMm = sug.requiredLengthMm ?? 0;
          const isMissing = !l.screwMaterialId;
          const isTooShort = currentLengthMm > 0 && currentLengthMm + 0.001 < requiredMm;
          if (!isMissing && !isTooShort) return l;
        }

        const newScrewId = sug.recommended.id;
        const newQty = Math.round(sug.screwsPerSqm);
        const newCost = computeScrewCostPerSqm(sug.recommended, newQty);
        const needsChange =
          l.screwMaterialId !== newScrewId ||
          Number(l.screwQuantity ?? -1) !== newQty ||
          Math.abs(Number(l.screwCostPerSqm ?? -1) - newCost) > 0.0001;
        if (!needsChange) return l;
        changed = true;
        return {
          ...l,
          screwMaterialId: newScrewId,
          screwMaterial: sug.recommended,
          screwQuantity: newQty,
          screwCostPerSqm: newCost,
          screwIsAutoSuggested: true,
        };
      });
      return changed ? next : prev;
    });
    if (changed) setIsDirty(true);
  }, [screwSuggestions]);

  const totalThicknessMm = useMemo(
    () => layers.reduce((sum, l) => sum + (Number(l.thickness) || 0), 0),
    [layers],
  );

  // --- Actions ---

  const setName = useCallback((v: string) => { setNameRaw(v); setIsDirty(true); }, []);
  const setDescription = useCallback((v: string) => { setDescriptionRaw(v); setIsDirty(true); }, []);

  const setCeiling = useCallback((patch: Partial<CeilingDimensionalState>) => {
    setCeilingState(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  }, []);

  /**
   * Auto-popola ceiling state da una tipologia sistema (CS-FIX-SINGLE / CS-SUSP-SINGLE /
   * CS-SUSP-DOUBLE / CS-FIRE). Override silenzioso dei defaults.
   */
  const applyTypology = useCallback((typology: {
    structure_type: 'single' | 'double';
    attachment_type: 'fixed' | 'suspended';
    hanger_spacing_mm: number | null;
    primary_profile_spacing_mm: number | null;
    secondary_profile_spacing_mm: number | null;
  }) => {
    setCeilingState(prev => ({
      ...prev,
      attachmentType: typology.attachment_type,
      hangerSpacingPrimaryMm: typology.hanger_spacing_mm ?? prev.hangerSpacingPrimaryMm,
      primarySpacingMm: typology.primary_profile_spacing_mm ?? prev.primarySpacingMm,
      // Per tipologie a contatto (fixed) il drop è 0 (no pendinatura), altrimenti mantiene default
      ceilingDropMm: typology.attachment_type === 'fixed' ? 0 : (prev.ceilingDropMm || 200),
      // Per orditura singola, secondary è null
      secondarySpacingMm: typology.structure_type === 'double'
        ? (typology.secondary_profile_spacing_mm ?? 500)
        : null,
    }));
    setIsDirty(true);
  }, []);

  const addLayer = useCallback((initial: Partial<LayerV2> = {}): string => {
    const layerId = newId();
    setLayers(prev => [
      ...prev,
      { id: layerId, position: prev.length + 1, materialId: null, material: null, thickness: 0, ...initial },
    ]);
    setIsDirty(true);
    return layerId;
  }, []);

  const updateLayer = useCallback((layerId: string, patch: Partial<LayerV2>) => {
    setLayers(prev => prev.map(l => (l.id === layerId ? { ...l, ...patch } : l)));
    setIsDirty(true);
  }, []);

  const removeLayer = useCallback((layerId: string) => {
    setLayers(prev => prev.filter(l => l.id !== layerId).map((l, i) => ({ ...l, position: i + 1 })));
    setIsDirty(true);
  }, []);

  const duplicateLayer = useCallback((layerId: string): string | null => {
    let cloneId: string | null = null;
    setLayers(prev => {
      const idx = prev.findIndex(l => l.id === layerId);
      if (idx < 0) return prev;
      const source = prev[idx];
      cloneId = newId();
      const clone: LayerV2 = { ...source, id: cloneId, position: source.position + 1 };
      const next = [...prev.slice(0, idx + 1), clone, ...prev.slice(idx + 1)];
      return next.map((l, i) => ({ ...l, position: i + 1 }));
    });
    setIsDirty(true);
    return cloneId;
  }, []);

  const reorderLayers = useCallback((orderedIds: string[]) => {
    setLayers(prev => {
      const map = new Map(prev.map(l => [l.id, l]));
      return orderedIds
        .map(id => map.get(id))
        .filter((l): l is LayerV2 => Boolean(l))
        .map((l, idx) => ({ ...l, position: idx + 1 }));
    });
    setIsDirty(true);
  }, []);

  const setMaterial = useCallback((layerId: string, material: DatabaseMaterial) => {
    setLayers(prev => prev.map(l => {
      if (l.id !== layerId) return l;
      // Per profili strutturali (orditura primaria/secondaria) lo spessore visibile
      // è la larghezza (`width`), non il `thickness` (sempre 0 in DB per i profili).
      const isStructureProfile =
        material.category === 'structure_frame' || material.category === 'structure_guide';
      const defaultThickness = isStructureProfile
        ? Number(material.width) || Number(material.thickness) || 0
        : Number(material.thickness) || 0;
      const thickness = l.thickness > 0 ? l.thickness : defaultThickness;
      return { ...l, materialId: material.id, material, thickness };
    }));
    setIsDirty(true);
  }, []);

  const setScrewMaterial = useCallback((layerId: string, screw: DatabaseMaterial | null) => {
    setLayers(prev => prev.map(l => {
      if (l.id !== layerId) return l;
      const qty = Number(l.screwQuantity ?? 0) > 0 ? Number(l.screwQuantity) : 17;
      const newCost = screw ? computeScrewCostPerSqm(screw, qty) : 0;
      return {
        ...l,
        screwMaterialId: screw?.id ?? null,
        screwMaterial: screw,
        screwQuantity: qty,
        screwCostPerSqm: newCost,
        screwIsAutoSuggested: false,
      };
    }));
    setIsDirty(true);
  }, []);

  const setScrewQuantity = useCallback((layerId: string, quantity: number) => {
    setLayers(prev => prev.map(l => {
      if (l.id !== layerId) return l;
      const qty = Math.max(0, Math.round(quantity));
      const newCost = l.screwMaterial ? computeScrewCostPerSqm(l.screwMaterial, qty) : 0;
      return { ...l, screwQuantity: qty, screwCostPerSqm: newCost, screwIsAutoSuggested: false };
    }));
    setIsDirty(true);
  }, []);

  const reapplyScrewSuggestion = useCallback((layerId: string) => {
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, screwIsAutoSuggested: true } : l));
    setIsDirty(true);
  }, []);

  const setCertification = useCallback(
    (patch: Partial<CertificationData> & { isCertified?: boolean }) => {
      if (typeof patch.isCertified === 'boolean') setIsCertified(patch.isCertified);
      setCertificationState(prev => {
        const next = { ...prev };
        (Object.keys(patch) as (keyof typeof patch)[]).forEach(key => {
          if (key === 'isCertified') return;
          const value = patch[key];
          if (value !== undefined) {
            (next as Record<string, unknown>)[key as string] = value as unknown;
          }
        });
        return next;
      });
      setIsDirty(true);
    },
    [],
  );

  const checkNameConflict = useCallback(async (): Promise<NameConflict | null> => {
    const trimmed = name.trim();
    if (!trimmed || !currentOrganizationId) return null;
    let query = supabase
      .from('stratigraphies')
      .select('id, name, is_certified')
      .eq('name', trimmed)
      .eq('organization_id', currentOrganizationId)
      .limit(1);
    if (id) query = query.neq('id', id);
    const { data, error } = await query.maybeSingle();
    if (error) {
      console.error('[useCeilingConfiguratorState] checkNameConflict error:', error);
      return null;
    }
    if (!data) return null;
    return { id: data.id, name: data.name, isCertified: !!data.is_certified };
  }, [name, id, currentOrganizationId]);

  const isValidName = name.trim().length > 0;
  const hasAtLeastOneCompleteLayer = layers.some(l => l.materialId && l.thickness > 0);
  const canSave = isValidName && hasAtLeastOneCompleteLayer && !saveMutation.isPending;

  /** Costruisce il payload per il save (forza type='ceiling' + campi ceiling-specific). */
  const buildSavePayload = useCallback(() => ({
    id: id ?? undefined,
    name: name.trim(),
    description,
    type: 'ceiling' as const,
    estimatedCost: 0,
    layers: layers.map(l => ({
      materialId: l.materialId,
      material: l.material,
      thickness: l.thickness,
      interAxis: l.interAxis,
      screwMaterialId: l.screwMaterialId,
      screwMaterial: l.screwMaterial,
      screwQuantity: l.screwQuantity,
      screwCostPerSqm: l.screwCostPerSqm,
    })),
    totalThickness: totalThicknessMm,
    weightPerSqm: certification.weightPerSqm ?? 0,
    studSpacingMm: ceiling.primarySpacingMm, // Riuso del campo DB stud_spacing_mm per il passo primario
    isCertified,
    // Certification metadata
    supplierName: certification.supplier,
    certificationCode: certification.certificationCode,
    certificationLab: certification.certificationLab,
    certificationDate: certification.certificationDate,
    certificationNotes: certification.certificationNotes,
    acousticPerformance: certification.acousticPerformance,
    acousticRwC: certification.acousticRwC,
    acousticRwCtr: certification.acousticRwCtr,
    acousticTestNorm: certification.acousticTestNorm,
    fireResistanceClass: certification.fireResistanceClass,
    fireReactionClass: certification.fireReactionClass,
    fireTestNorm: certification.fireTestNorm,
    thermalPerformance: certification.thermalPerformance,
    thermalUValue: certification.thermalUValue,
    thermalRValue: certification.thermalRValue,
    mechanicalMaxHeightM: certification.mechanicalMaxHeightM,
    mechanicalLoadNSqm: certification.mechanicalLoadNSqm,
    mechanicalSuspendableLoadKg: certification.mechanicalSuspendableLoadKg,
    // ---- Ceiling-specific (passthrough nel save hook) ----
    ceilingDropMm: ceiling.ceilingDropMm,
    attachmentType: ceiling.attachmentType,
    hangerSpacingPrimaryMm: ceiling.hangerSpacingPrimaryMm,
    hangerSpacingPerpendicularMm: ceiling.hangerSpacingPerpendicularMm,
    secondarySpacingMm: ceiling.secondarySpacingMm,
    perimeterProfileMlPerSqm: ceiling.perimeterProfileMlPerSqm,
  }), [id, name, description, layers, totalThicknessMm, ceiling, isCertified, certification]);

  const save = useCallback(() => {
    if (!canSave) {
      if (!isValidName) toast.error('Inserisci un nome per il controsoffitto');
      else toast.error('Aggiungi almeno un layer completo prima di salvare');
      return;
    }
    saveMutation.mutate(buildSavePayload(), {
      onSuccess: (res) => {
        setIsDirty(false);
        if (res?.id && !id) setId(res.id);
      },
    });
  }, [canSave, isValidName, saveMutation, id, buildSavePayload]);

  const saveAsync = useCallback(async (): Promise<string | null> => {
    if (id && !isDirty) return id;
    if (!canSave) {
      if (!isValidName) toast.error('Inserisci un nome per il controsoffitto');
      else toast.error('Aggiungi almeno un layer completo prima di salvare');
      return null;
    }
    try {
      const res = await saveMutation.mutateAsync(buildSavePayload());
      const savedId = (res as { id?: string } | undefined)?.id ?? id;
      if (savedId && !id) setId(savedId);
      setIsDirty(false);
      return savedId ?? null;
    } catch (err) {
      console.error('[useCeilingConfiguratorState] saveAsync error:', err);
      const message = (err as Error)?.message ?? 'Errore durante il salvataggio.';
      toast.error(`Errore salvataggio: ${message}`);
      return null;
    }
  }, [canSave, isValidName, isDirty, saveMutation, id, buildSavePayload]);

  return {
    id, name, description, isCertified, certification, layers,
    isLoading, isDirty, isSaving: saveMutation.isPending,
    loadError: loadError ? String((loadError as Error).message ?? loadError) : null,
    totalThicknessMm,
    ceiling,
    setName, setDescription, addLayer, updateLayer, removeLayer, duplicateLayer,
    reorderLayers, setMaterial, setCertification,
    setScrewMaterial, setScrewQuantity, reapplyScrewSuggestion,
    setCeiling, applyTypology,
    checkNameConflict, saveAsync, save, canSave,
  };
}
